import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BookOpen,
  ExternalLink,
  MessageSquare,
  Network,
  Pin,
  RefreshCw,
  Search,
} from "lucide-react";
import { useMemo, useState } from "react";
import { api } from "../../lib/api";

interface Node {
  id: string;
  label: string;
  kind: "doc" | "tag" | "collection" | "note";
  project: string;
  x: number;
  y: number;
  size: number;
}
interface Edge {
  id: string;
  source: string;
  target: string;
  weight: number;
  relation: "ref" | "tag" | "contains";
}

const NODES: Node[] = [
  {
    id: "n-runbook",
    label: "Despliegue continuo",
    kind: "doc",
    project: "netbox",
    x: 380,
    y: 240,
    size: 16,
  },
  {
    id: "n-bootstrap",
    label: "Bootstrap entorno",
    kind: "doc",
    project: "netbox",
    x: 200,
    y: 160,
    size: 13,
  },
  {
    id: "n-tls",
    label: "CF Access · TLS",
    kind: "doc",
    project: "platform",
    x: 560,
    y: 140,
    size: 13,
  },
  {
    id: "n-secrets",
    label: "1Password vault",
    kind: "doc",
    project: "platform",
    x: 620,
    y: 340,
    size: 12,
  },
  { id: "n-d1", label: "D1 migraciones", kind: "doc", project: "netbox", x: 240, y: 380, size: 12 },
  {
    id: "n-tail",
    label: "Manual Tailscale",
    kind: "doc",
    project: "platform",
    x: 400,
    y: 80,
    size: 11,
  },
  {
    id: "n-postm",
    label: "Postmortem TLS abr/26",
    kind: "doc",
    project: "platform",
    x: 700,
    y: 220,
    size: 11,
  },
  { id: "n-cflare", label: "cloudflare", kind: "tag", project: "—", x: 470, y: 340, size: 8 },
  { id: "n-wrangler", label: "wrangler", kind: "tag", project: "—", x: 310, y: 320, size: 8 },
  { id: "n-tls-tag", label: "tls", kind: "tag", project: "—", x: 630, y: 200, size: 7 },
  {
    id: "n-col",
    label: "NetBox onboarding Q2",
    kind: "collection",
    project: "—",
    x: 300,
    y: 470,
    size: 14,
  },
  {
    id: "n-note",
    label: "Workaround macOS M1",
    kind: "note",
    project: "—",
    x: 480,
    y: 460,
    size: 9,
  },
];

const EDGES: Edge[] = [
  { id: "e1", source: "n-runbook", target: "n-bootstrap", weight: 0.9, relation: "ref" },
  { id: "e2", source: "n-runbook", target: "n-tls", weight: 0.85, relation: "ref" },
  { id: "e3", source: "n-runbook", target: "n-secrets", weight: 0.78, relation: "ref" },
  { id: "e4", source: "n-runbook", target: "n-d1", weight: 0.72, relation: "ref" },
  { id: "e5", source: "n-runbook", target: "n-cflare", weight: 1, relation: "tag" },
  { id: "e6", source: "n-runbook", target: "n-wrangler", weight: 1, relation: "tag" },
  { id: "e7", source: "n-tls", target: "n-tls-tag", weight: 1, relation: "tag" },
  { id: "e8", source: "n-postm", target: "n-tls-tag", weight: 1, relation: "tag" },
  { id: "e9", source: "n-postm", target: "n-tls", weight: 0.92, relation: "ref" },
  { id: "e10", source: "n-bootstrap", target: "n-d1", weight: 0.65, relation: "ref" },
  { id: "e11", source: "n-bootstrap", target: "n-tail", weight: 0.6, relation: "ref" },
  { id: "e12", source: "n-col", target: "n-runbook", weight: 1, relation: "contains" },
  { id: "e13", source: "n-col", target: "n-bootstrap", weight: 1, relation: "contains" },
  { id: "e14", source: "n-col", target: "n-secrets", weight: 1, relation: "contains" },
  { id: "e15", source: "n-col", target: "n-note", weight: 1, relation: "contains" },
  { id: "e16", source: "n-note", target: "n-wrangler", weight: 1, relation: "tag" },
];

const KIND_COLOR: Record<Node["kind"], string> = {
  doc: "var(--accent-500)",
  tag: "oklch(0.65 0.14 75)",
  collection: "oklch(0.55 0.18 250)",
  note: "oklch(0.55 0.12 220)",
};

const RELATION_LABEL: Record<Edge["relation"], string> = {
  ref: "Referencia",
  tag: "Etiqueta",
  contains: "Contención",
};

type Scope = "doc" | "col" | "proj" | "all";

export function GraphView() {
  const [scope, setScope] = useState<Scope>("col");
  const [minConfidence, setMinConfidence] = useState(60);
  const [depth, setDepth] = useState(2);
  const [showRef, setShowRef] = useState(true);
  const [showTag, setShowTag] = useState(true);
  const [showContains, setShowContains] = useState(true);

  // B6.4 — pedimos al backend. Si la tabla semantic_edges aún no tiene filas
  // (staging seed pendiente), caemos al mock para que la UI sea inspectable.
  const graphQuery = useQuery({
    queryKey: ["graph", { scope, depth, minConfidence }],
    queryFn: () =>
      api.getGraph({ scope, depth, min_confidence: minConfidence / 100 }).catch(() => null),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
  const {
    nodes: liveNodes,
    edges: liveEdges,
    usingLive,
  } = useMemo(() => {
    const data = graphQuery.data;
    if (!data || data.nodes.length === 0 || data.edges.length === 0) {
      return { nodes: NODES, edges: EDGES, usingLive: false };
    }
    return {
      nodes: layoutNodes(
        data.nodes.map((n) => ({
          id: `${n.type}:${n.id}`,
          label: n.label,
          kind: nodeKindOf(n.type),
          project: n.project ?? "—",
        })),
      ),
      edges: data.edges.map((e) => ({
        id: e.id,
        source: `${e.source_type}:${e.source_id}`,
        target: `${e.target_type}:${e.target_id}`,
        weight: e.confidence ?? e.weight,
        relation: relationOf(e.relation),
      })),
      usingLive: true,
    };
  }, [graphQuery.data]);

  const [selected, setSelected] = useState<string>(liveNodes[0]?.id ?? "n-runbook");

  const visibleEdges = useMemo(
    () =>
      liveEdges
        .filter((e) => Math.round(e.weight * 100) >= minConfidence)
        .filter((e) => {
          if (e.relation === "ref" && !showRef) return false;
          if (e.relation === "tag" && !showTag) return false;
          if (e.relation === "contains" && !showContains) return false;
          return true;
        }),
    [liveEdges, minConfidence, showRef, showTag, showContains],
  );

  const relatedToSelected = useMemo(
    () =>
      visibleEdges
        .filter((e) => e.source === selected || e.target === selected)
        .map((e) => ({
          edge: e,
          otherId: e.source === selected ? e.target : e.source,
        })),
    [visibleEdges, selected],
  );

  const selNode = liveNodes.find((n) => n.id === selected);

  return (
    <div className="flex h-full flex-col" data-testid="graph-screen">
      <header className="flex items-center justify-between border-b border-(--border-subtle) bg-(--bg-elevated) px-6 py-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Network size={18} /> Graph
          </h1>
          <p className="font-mono text-[11px] text-(--text-tertiary)" data-testid="graph-meta">
            {liveNodes.length} nodos · {visibleEdges.length} relaciones visibles ·{" "}
            <span
              data-testid="graph-source"
              data-live={usingLive ? "true" : "false"}
              className={usingLive ? "text-(--accent-600)" : ""}
            >
              {usingLive ? "datos reales" : "datos mock"}
            </span>
          </p>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Left controls */}
        <aside
          data-testid="graph-controls"
          className="w-64 space-y-4 overflow-y-auto border-r border-(--border-subtle) bg-(--bg-elevated) p-4 text-sm"
        >
          <ControlBlock title="Scope">
            <div className="flex flex-col gap-1">
              {(
                [
                  { id: "doc" as Scope, label: "Documento actual" },
                  { id: "col" as Scope, label: "Colección actual" },
                  { id: "proj" as Scope, label: "Proyecto netbox" },
                  { id: "all" as Scope, label: "Todo" },
                ] as const
              ).map((s) => (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => setScope(s.id)}
                  data-testid={`graph-scope-${s.id}`}
                  data-active={scope === s.id ? "true" : "false"}
                  className={`rounded-md border px-2.5 py-1 text-left text-xs transition ${
                    scope === s.id
                      ? "border-(--accent-400) bg-(--accent-50) text-(--accent-700)"
                      : "border-(--border-subtle) bg-(--bg-canvas) hover:bg-(--bg-hover)"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </ControlBlock>

          <ControlBlock title="Parámetros">
            <div className="space-y-2.5">
              <Slider
                label="Profundidad"
                value={depth}
                min={1}
                max={4}
                step={1}
                onChange={setDepth}
                testId="graph-depth"
                displayValue={String(depth)}
              />
              <Slider
                label="Confianza mín."
                value={minConfidence}
                min={0}
                max={100}
                step={5}
                onChange={setMinConfidence}
                testId="graph-confidence"
                displayValue={`${minConfidence}%`}
              />
            </div>
          </ControlBlock>

          <ControlBlock title="Tipo de relación">
            <RelationCheck
              label="Referencias"
              testId="graph-rel-ref"
              checked={showRef}
              onChange={setShowRef}
              color="var(--text-primary)"
            />
            <RelationCheck
              label="Etiquetas"
              testId="graph-rel-tag"
              checked={showTag}
              onChange={setShowTag}
              color="var(--accent-500)"
            />
            <RelationCheck
              label="Contención"
              testId="graph-rel-contains"
              checked={showContains}
              onChange={setShowContains}
              color="oklch(0.55 0.18 250)"
            />
          </ControlBlock>

          <ControlBlock title="Densidad">
            <button
              type="button"
              data-testid="graph-relayout"
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2.5 py-1.5 text-xs hover:bg-(--bg-hover)"
            >
              <RefreshCw size={11} /> Reorganizar
            </button>
          </ControlBlock>
        </aside>

        {/* Canvas */}
        <main className="relative flex-1 overflow-auto bg-(--bg-canvas)" data-testid="graph-canvas">
          <div className="absolute right-3 top-3 z-10 inline-flex items-center gap-1 rounded-md border border-(--warning) bg-(--warning-soft) px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-(--warning)">
            ⚠ Datos mock · sin embeddings
          </div>
          <svg viewBox="0 0 840 600" className="h-full w-full">
            <title>Grafo semántico</title>
            <defs>
              <pattern id="dotgrid" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="0.8" fill="var(--border-subtle)" />
              </pattern>
            </defs>
            <rect width="840" height="600" fill="url(#dotgrid)" opacity="0.6" />

            {visibleEdges.map((e) => {
              const s = liveNodes.find((n) => n.id === e.source);
              const t = liveNodes.find((n) => n.id === e.target);
              if (!s || !t) return null;
              const isHot = e.source === selected || e.target === selected;
              return (
                <line
                  key={e.id}
                  data-testid="graph-edge"
                  data-relation={e.relation}
                  x1={s.x}
                  y1={s.y}
                  x2={t.x}
                  y2={t.y}
                  stroke={
                    e.relation === "tag"
                      ? "var(--accent-400)"
                      : e.relation === "contains"
                        ? "oklch(0.55 0.18 250)"
                        : "var(--border-strong)"
                  }
                  strokeOpacity={isHot ? 0.9 : 0.35}
                  strokeWidth={isHot ? 2.5 : 1 + e.weight * 1.5}
                  strokeDasharray={e.relation === "tag" ? "4 4" : undefined}
                />
              );
            })}
            {liveNodes.map((n) => {
              const isSel = n.id === selected;
              return (
                // biome-ignore lint/a11y/useKeyWithClickEvents: SVG graph nodes are pointer-only; keyboard nav comes via the related-list on the right
                <g
                  key={n.id}
                  data-testid="graph-node"
                  data-node-id={n.id}
                  data-kind={n.kind}
                  transform={`translate(${n.x},${n.y})`}
                  onClick={() => setSelected(n.id)}
                  className="cursor-pointer"
                >
                  <circle
                    r={n.size}
                    fill={KIND_COLOR[n.kind]}
                    fillOpacity={isSel ? 1 : 0.7}
                    stroke={isSel ? "var(--text-primary)" : "transparent"}
                    strokeWidth={isSel ? 2 : 0}
                  />
                  <text
                    y={n.size + 12}
                    textAnchor="middle"
                    className="fill-(--text-secondary) text-[10px]"
                    pointerEvents="none"
                  >
                    {n.label}
                  </text>
                </g>
              );
            })}
          </svg>

          <div
            className="pointer-events-none absolute bottom-3 left-3 inline-flex gap-3 rounded-md border border-(--border-subtle) bg-(--bg-canvas)/90 px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider"
            data-testid="graph-legend"
          >
            {Object.entries(KIND_COLOR).map(([k, c]) => (
              <span key={k} className="inline-flex items-center gap-1">
                <span className="h-2 w-2 rounded-full" style={{ background: c }} />
                {k}
              </span>
            ))}
          </div>
        </main>

        {/* Right detail */}
        <aside
          data-testid="graph-detail"
          className="w-72 space-y-3 overflow-y-auto border-l border-(--border-subtle) bg-(--bg-elevated) p-4 text-sm"
        >
          {selNode ? (
            <>
              <header>
                <p className="text-[10px] font-mono uppercase tracking-wider text-(--text-tertiary)">
                  {selNode.kind}
                </p>
                <h3 className="text-base font-semibold text-(--text-primary)">{selNode.label}</h3>
              </header>
              <div className="flex flex-wrap gap-1">
                {selNode.kind === "doc" && (
                  <Link
                    to="/docs"
                    data-testid="graph-detail-open"
                    className="inline-flex items-center gap-1 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2 py-1 text-xs no-underline hover:bg-(--bg-hover)"
                  >
                    <ExternalLink size={11} /> Abrir
                  </Link>
                )}
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2 py-1 text-xs hover:bg-(--bg-hover)"
                >
                  <BookOpen size={11} /> A apuntes
                </button>
                <button
                  type="button"
                  aria-label="Fijar nodo"
                  title="Fijar nodo"
                  className="inline-flex items-center gap-1 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2 py-1 text-xs hover:bg-(--bg-hover)"
                >
                  <Pin size={11} />
                </button>
              </div>

              <section>
                <header className="mb-1 text-[10px] font-mono uppercase tracking-wider text-(--text-tertiary)">
                  Vinculado con
                </header>
                {relatedToSelected.length === 0 ? (
                  <p className="text-xs text-(--text-tertiary)">
                    Sin relaciones visibles. Sube la confianza o cambia el scope.
                  </p>
                ) : (
                  <ul className="space-y-1" data-testid="graph-detail-related">
                    {relatedToSelected.map(({ edge, otherId }) => {
                      const other = liveNodes.find((n) => n.id === otherId);
                      if (!other) return null;
                      return (
                        <li
                          key={edge.id}
                          className="flex items-center justify-between gap-2 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2 py-1 text-xs"
                        >
                          <button
                            type="button"
                            onClick={() => setSelected(other.id)}
                            className="flex flex-1 items-center gap-1.5 min-w-0 text-left"
                          >
                            <span
                              className="rounded-full px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wide"
                              style={{
                                background:
                                  edge.relation === "tag"
                                    ? "var(--accent-50)"
                                    : "var(--bg-elevated)",
                                color:
                                  edge.relation === "tag"
                                    ? "var(--accent-700)"
                                    : "var(--text-secondary)",
                              }}
                            >
                              {RELATION_LABEL[edge.relation]}
                            </span>
                            <span className="flex-1 truncate">{other.label}</span>
                          </button>
                          <span className="font-mono text-[10px] text-(--text-tertiary)">
                            {Math.round(edge.weight * 100)}%
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              <section>
                <header className="mb-1 text-[10px] font-mono uppercase tracking-wider text-(--text-tertiary)">
                  Acciones
                </header>
                <div className="space-y-1">
                  <DetailAction icon={<Search size={11} />} label="Buscar similares" />
                  <DetailAction
                    icon={<MessageSquare size={11} />}
                    label="Preguntar sobre este nodo"
                  />
                </div>
              </section>
            </>
          ) : (
            <p className="text-xs text-(--text-tertiary)">Selecciona un nodo en el graph.</p>
          )}
        </aside>
      </div>
    </div>
  );
}

function ControlBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-1.5 text-[10px] font-mono uppercase tracking-wider text-(--text-tertiary)">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  testId,
  displayValue,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  testId?: string;
  displayValue?: string;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between text-xs">
        <label htmlFor={testId} className="text-(--text-secondary)">
          {label}
        </label>
        <span className="font-mono text-[11px] text-(--text-primary)">{displayValue ?? value}</span>
      </div>
      <input
        id={testId}
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testId}
        className="w-full"
      />
    </div>
  );
}

function RelationCheck({
  label,
  checked,
  onChange,
  color,
  testId,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  color: string;
  testId?: string;
}) {
  return (
    <label
      className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-xs hover:bg-(--bg-hover)"
      data-testid={testId}
      data-checked={checked}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded"
      />
      <span className="flex-1">{label}</span>
      <span className="h-0.5 w-4" style={{ background: color, opacity: checked ? 1 : 0.3 }} />
    </label>
  );
}

function DetailAction({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <button
      type="button"
      className="flex w-full items-center gap-1.5 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2 py-1.5 text-left text-xs hover:bg-(--bg-hover)"
    >
      <span className="text-(--text-tertiary)">{icon}</span>
      <span>{label}</span>
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// B6.4 — helpers for live data
// ──────────────────────────────────────────────────────────────────────────────

function nodeKindOf(type: string): Node["kind"] {
  if (type === "doc") return "doc";
  if (type === "tag") return "tag";
  if (type === "collection" || type === "col") return "collection";
  if (type === "note" || type === "notebook") return "note";
  return "doc";
}

function relationOf(rel: string): Edge["relation"] {
  if (rel === "tag" || rel === "tags") return "tag";
  if (rel === "contains" || rel === "membership") return "contains";
  return "ref";
}

/**
 * Deterministic radial layout: nodes are placed around a circle, with bigger
 * cluster radius for tag/note kinds vs core doc nodes. Same node id → same
 * position across renders (good for snapshot tests).
 */
function layoutNodes(items: Array<Omit<Node, "x" | "y" | "size">>): Node[] {
  if (items.length === 0) return [];
  const cx = 420;
  const cy = 280;
  const ringByKind: Record<Node["kind"], number> = {
    doc: 170,
    tag: 240,
    collection: 100,
    note: 220,
  };
  // Sort by id to get a stable angular order
  const ordered = [...items].sort((a, b) => a.id.localeCompare(b.id));
  const step = (2 * Math.PI) / ordered.length;
  return ordered.map((n, idx) => {
    const angle = idx * step;
    const radius = ringByKind[n.kind] ?? 180;
    const sizeByKind: Record<Node["kind"], number> = {
      doc: 14,
      tag: 9,
      collection: 16,
      note: 11,
    };
    return {
      ...n,
      x: Math.round(cx + Math.cos(angle) * radius),
      y: Math.round(cy + Math.sin(angle) * radius),
      size: sizeByKind[n.kind] ?? 12,
    };
  });
}
